"use client";

import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import { EmptyState } from "@/components/ui/EmptyState";
import { AIAssistantModal } from "@/components/ui/AIAssistantModal";
import { formatCurrency } from "@/lib/utils";

interface JewelryItem {
  id: string;
  sku: string;
  serialNumber: string | null;
  name: string;
  description: string | null;
  category: string;
  brand: string | null;
  collection: string | null;
  style: string | null;
  cost: number;
  retailPrice: number;
  salePrice: number | null;
  metalType: string | null;
  metalWeight: string | null;
  ringSize: string | null;
  length: string | null;
  primaryStoneType: string | null;
  primaryStoneCarat: string | null;
  primaryStoneCut: string | null;
  primaryStoneClarity: string | null;
  primaryStoneColor: string | null;
  primaryStoneCertification: string | null;
  totalCaratWeight: string | null;
  status: string;
  location: string | null;
  photoUrls: string[];
  tags: string[];
  createdAt: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  RING: "Rings",
  NECKLACE: "Necklaces",
  BRACELET: "Bracelets",
  EARRINGS: "Earrings",
  WATCH: "Watches",
  PENDANT: "Pendants",
  BROOCH: "Brooches",
  CUFFLINKS: "Cufflinks",
  CHAIN: "Chains",
  LOOSE_STONE: "Loose Stones",
  OTHER: "Other",
};

const METAL_LABELS: Record<string, string> = {
  GOLD_10K: "10k Gold",
  GOLD_14K: "14k Gold",
  GOLD_18K: "18k Gold",
  GOLD_22K: "22k Gold",
  GOLD_24K: "24k Gold",
  WHITE_GOLD_14K: "14k White Gold",
  WHITE_GOLD_18K: "18k White Gold",
  ROSE_GOLD_14K: "14k Rose Gold",
  ROSE_GOLD_18K: "18k Rose Gold",
  PLATINUM: "Platinum",
  PALLADIUM: "Palladium",
  STERLING_SILVER: "Sterling Silver",
  TITANIUM: "Titanium",
  TUNGSTEN: "Tungsten",
  STAINLESS_STEEL: "Steel",
  OTHER: "Other",
};

const STATUS_VARIANT: Record<string, "default" | "success" | "warning" | "danger" | "info"> = {
  IN_STOCK: "success",
  SOLD: "default",
  MEMO_OUT: "warning",
  LAYAWAY: "info",
  IN_REPAIR: "warning",
  HOLD: "warning",
  RETURNED: "danger",
};

const STATUS_LABELS: Record<string, string> = {
  IN_STOCK: "In stock",
  SOLD: "Sold",
  MEMO_OUT: "Memo out",
  LAYAWAY: "Layaway",
  IN_REPAIR: "In repair",
  HOLD: "Hold",
  RETURNED: "Returned",
};

export default function JewelryInventoryPage() {
  const [items, setItems] = useState<JewelryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showNewModal, setShowNewModal] = useState(false);
  const [category, setCategory] = useState<string>("ALL");
  const [metal, setMetal] = useState<string>("ALL");
  const [status, setStatus] = useState<string>("ALL");

  useEffect(() => {
    const run = async () => {
      try {
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (category !== "ALL") params.set("category", category);
        if (metal !== "ALL") params.set("metal", metal);
        if (status !== "ALL") params.set("status", status);
        const res = await fetch(`/api/jewelry-items?${params.toString()}`);
        if (res.ok) {
          const data = await res.json();
          setItems(data.data || []);
        }
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [search, category, metal, status]);

  const summary = useMemo(() => {
    const totalRetail = items.reduce((s, i) => s + i.retailPrice, 0);
    const inStock = items.filter((i) => i.status === "IN_STOCK").length;
    const onMemo = items.filter((i) => i.status === "MEMO_OUT").length;
    const onLayaway = items.filter((i) => i.status === "LAYAWAY").length;
    return { totalRetail, inStock, onMemo, onLayaway };
  }, [items]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white">Inventory</h1>
          <p className="mt-1 text-sm text-muted">
            Serialized jewelry inventory with 4Cs, metal, and stone attributes.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNewModal(true)}
          className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-black transition hover:bg-white/90"
        >
          + New item
        </button>
      </div>

      <AIAssistantModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        entity="inventory item"
        example="Add new inventory: 3.01ct oval diamond, G color, VS1, cost 48k, retail 95k."
      />

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">Total retail</p>
          <p className="mt-1 text-xl font-semibold text-white">{formatCurrency(summary.totalRetail)}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">In stock</p>
          <p className="mt-1 text-xl font-semibold text-white">{summary.inStock}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">On memo</p>
          <p className="mt-1 text-xl font-semibold text-amber-200">{summary.onMemo}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">On layaway</p>
          <p className="mt-1 text-xl font-semibold text-white">{summary.onLayaway}</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input
          placeholder="Search by SKU, name, brand, stone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="min-w-[280px] flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-white/30 focus:outline-none"
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white focus:border-white/30 focus:outline-none"
        >
          <option value="ALL">All categories</option>
          {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <select
          value={metal}
          onChange={(e) => setMetal(e.target.value)}
          className="rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white focus:border-white/30 focus:outline-none"
        >
          <option value="ALL">All metals</option>
          {Object.entries(METAL_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white focus:border-white/30 focus:outline-none"
        >
          <option value="ALL">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : items.length === 0 ? (
        <EmptyState
          title="No inventory yet"
          description="Load the jewelry demo data from the dashboard to explore sample inventory."
          icon={
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((item) => (
            <Card key={item.id} className="group overflow-hidden transition hover:border-white/20">
              <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-white/5 to-transparent">
                {item.photoUrls?.[0] ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={item.photoUrls[0]}
                    alt={item.name}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-white/20">
                    <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                )}
                <div className="absolute left-2 top-2">
                  <Badge variant={STATUS_VARIANT[item.status] || "default"} size="sm">
                    {STATUS_LABELS[item.status] || item.status}
                  </Badge>
                </div>
                {item.brand && (
                  <div className="absolute right-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-medium text-amber-200 backdrop-blur">
                    {item.brand}
                  </div>
                )}
              </div>
              <CardContent className="space-y-2 py-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-white">{item.name}</p>
                    <p className="text-xs text-text-dim">{item.sku}</p>
                  </div>
                  <p className="text-right text-sm font-semibold text-white">
                    {formatCurrency(item.retailPrice)}
                  </p>
                </div>

                {/* 4Cs / Stone row */}
                {item.primaryStoneType === "DIAMOND" && (item.primaryStoneCarat || item.primaryStoneCut) && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {item.primaryStoneCarat && (
                      <span className="rounded-md bg-amber-200/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-200">
                        {item.primaryStoneCarat}ct
                      </span>
                    )}
                    {item.primaryStoneCut && (
                      <span className="rounded-md bg-white/5 px-1.5 py-0.5 text-[10px] text-white/70">
                        {item.primaryStoneCut}
                      </span>
                    )}
                    {item.primaryStoneClarity && (
                      <span className="rounded-md bg-white/5 px-1.5 py-0.5 text-[10px] text-white/70">
                        {item.primaryStoneClarity}
                      </span>
                    )}
                    {item.primaryStoneColor && (
                      <span className="rounded-md bg-white/5 px-1.5 py-0.5 text-[10px] text-white/70">
                        {item.primaryStoneColor}
                      </span>
                    )}
                    {item.primaryStoneCertification && (
                      <span className="rounded-md border border-amber-200/30 px-1.5 py-0.5 text-[10px] text-amber-200/80">
                        {item.primaryStoneCertification}
                      </span>
                    )}
                  </div>
                )}
                {item.primaryStoneType && item.primaryStoneType !== "DIAMOND" && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <span className="rounded-md bg-amber-200/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-200">
                      {item.primaryStoneType.charAt(0) + item.primaryStoneType.slice(1).toLowerCase()}
                    </span>
                    {item.totalCaratWeight && (
                      <span className="rounded-md bg-white/5 px-1.5 py-0.5 text-[10px] text-white/70">
                        {item.totalCaratWeight}ctw
                      </span>
                    )}
                  </div>
                )}

                {/* Metal / size row */}
                <div className="flex flex-wrap gap-1.5 text-[10px] text-text-dim">
                  {item.metalType && <span>{METAL_LABELS[item.metalType] || item.metalType}</span>}
                  {item.ringSize && <span>· Size {item.ringSize}</span>}
                  {item.length && <span>· {item.length}</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
